import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

type RailNavButtonProps = {
  label?: string;
  icon: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isActive?: boolean;
  buttonTitle?: string;
  sx?: React.CSSProperties;
  navType?: 'internal' | 'modal' | 'external';
};

function RailNavButton(
  props: Readonly<RailNavButtonProps & { tabIndex?: number }>,
) {
  return (
    <StyledRailButton
      aria-label={`${props.label ?? props.buttonTitle} rail button`}
      onClick={props.onClick}
      active={props.isActive}
      navType={props.navType}
      title={props.buttonTitle ?? props.label}
      tabIndex={props.tabIndex}
    >
      <span data-slot="icon"> {props.icon}</span>
      <span data-slot="label"> {props.label}</span>
    </StyledRailButton>
  );
}

export function RailNavExternalLink(
  props: Readonly<RailNavButtonProps & { href?: string }>,
) {
  return (
    <ExternalLinkWrapper>
      <a href={props.href} target="_blank" rel="noopener noreferrer">
        {/* negative tabIndex to prevent focus, since outer link handles focus */}
        <RailNavButton {...props} tabIndex={-1} />
      </a>
      <ExternalLinkBadge aria-hidden="true">
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </ExternalLinkBadge>
    </ExternalLinkWrapper>
  );
}

export function RailNavLink(
  props: Readonly<RailNavButtonProps & { to: string }>,
) {
  return (
    <Link to={props.to}>
      {/* negative tabIndex to prevent focus, since outer link handles focus */}
      <RailNavButton {...props} tabIndex={-1} />
    </Link>
  );
}

export function RailModalButton(
  props: Readonly<RailNavButtonProps & { anchorId?: string }>,
) {
  const { anchorId, ...buttonProps } = props;
  return (
    <div data-rail-modal-wrapper data-modal-anchor={anchorId}>
      <RailNavButton {...buttonProps} />
    </div>
  );
}

const BaseRailButton = styled.button<{ active: boolean }>`
  width: 100%;
  height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  overflow: visible;

  font-size: 0.75rem;
  font-weight: 500;
  line-height: 0.8rem;
  letter-spacing: -0.02em;

  color: ${(props) => props.theme.colors.blue[600]};
  background-color: transparent;
  --rail-hover-bg: ${(props) => props.theme.colors.blue[100]};
  --rail-hover-color: ${(props) => props.theme.colors.blue[700]};
  --rail-active-bg: ${(props) => props.theme.colors.blue[700]};
  --rail-active-color: ${(props) => props.theme.colors.white};
  transition:
    background-color 0.3s ease,
    filter 0.3s ease,
    color 0.3s ease;

  cursor: pointer;

  [data-slot='icon'] {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 35px;
    height: 35px;
    font-size: 1.1rem;

    --fa-primary-color: ${(props) =>
      props.active ? props.theme.colors.white : props.theme.colors.blue[600]};
    --fa-secondary-color: ${(props) =>
      props.active ? props.theme.colors.white : props.theme.colors.blue[600]};

    svg {
      width: 'inherit';
    }
  }

  [data-slot='label'] {
    text-align: center;
    font-weight: 500;
  }
`;

const InternalNavButton = styled(BaseRailButton)`
  background-color: white;

  ${(props) =>
    props.active &&
    css`
      && {
        color: ${props.theme.colors.white};
        background-color: ${props.theme.colors.blue[700]};
        [data-slot='icon'] {
          color: ${props.theme.colors.white};
          --fa-primary-color: ${props.theme.colors.white};
          --fa-secondary-color: ${props.theme.colors.white};
          svg {
            color: ${props.theme.colors.white};
          }
        }
      }
    `}

  ${(props) =>
    !props.active &&
    css`
      &:hover,
      a:hover & {
        background-color: ${props.theme.colors.blue[100]};
        color: ${props.theme.colors.blue[700]};
        [data-slot='icon'] {
          color: ${props.theme.colors.blue[700]};
          --fa-primary-color: ${props.theme.colors.blue[700]};
          --fa-secondary-color: ${props.theme.colors.blue[700]};
          svg {
            color: ${props.theme.colors.blue[700]};
          }
          img {
            filter: brightness(0.85) saturate(1.2);
          }
        }
      }
    `}
`;

const ModalNavButton = styled(BaseRailButton)`
  ${(props) =>
    props.active &&
    css`
      && {
        color: ${props.theme.colors.white};
        background-color: ${props.theme.colors.blue[700]};
        [data-slot='icon'] {
          color: ${props.theme.colors.white};
          --fa-primary-color: ${props.theme.colors.white};
          --fa-secondary-color: ${props.theme.colors.white};
          svg {
            color: ${props.theme.colors.white};
          }
        }
      }
    `}

  ${(props) =>
    !props.active &&
    css`
      [data-rail-modal-wrapper]:hover & {
        background-color: white;
        color: ${props.theme.colors.blue[600]};
      }
    `}
`;

const StyledRailButton = ({
  navType,
  ...props
}: React.ComponentProps<typeof BaseRailButton> & {
  navType?: 'internal' | 'modal' | 'external';
}) =>
  navType === 'modal' ? (
    <ModalNavButton {...props} />
  ) : (
    <InternalNavButton {...props} />
  );

const ExternalLinkWrapper = styled.div`
  position: relative;
`;

const ExternalLinkBadge = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  font-size: 0.75rem;
  line-height: 1;
  color: ${(props) => props.theme.colors.blue[700]};
  pointer-events: none;
`;
